import { createFileRoute } from "@tanstack/react-router";

import { PageChrome } from "../components/PageChrome";
import { StoreCta } from "../components/StoreCta";
import { COMPANY, COMPANY_ADDRESS, COMPANY_NO, SUPPORT } from "../lib/legal";
import {
  breadcrumbJsonLd,
  canonical,
  documentHeaders,
  organizationJsonLd,
  socialMeta,
} from "../lib/seo";

const TITLE = "Who makes Bible Shield — About";
const DESCRIPTION =
  "Who builds Bible Shield, where the company is registered, how the app is paid for, and what it does with what you write. Written plainly, checkable.";

/**
 * `/about` — the page that was a 404.
 *
 * ── Why a small site needs one ───────────────────────────────────────────────
 *
 * Two audiences read this page and neither is browsing. The first is a person
 * deciding whether to hand a prayer habit to software made by strangers; the
 * question they have is who, and it is a fair one. The second is a search engine
 * or an assistant trying to resolve "Bible Shield" to an entity it can attach
 * facts to — and an entity with a registered company number, an address and a
 * support address is one it can, while a brand name with none of those is a
 * string.
 *
 * ── What is deliberately NOT here ────────────────────────────────────────────
 *
 * A named author with a photograph and a paragraph of credentials. Every guide
 * to this subject asks for one, and inventing a person is the single easiest
 * thing on that list to do and the only one that would be a lie. The company is
 * real, its number is checkable at Companies House, and until a human decides to
 * put their own name to this writing, the honest byline is the organisation.
 *
 * Everything stated below is either a fact from `lib/legal.ts` — the same
 * constants the privacy policy and the terms are built from, checked by
 * `legal.test.ts` against what the app actually does — or a description of
 * behaviour anyone can verify by using it.
 */
export const Route = createFileRoute("/about")({
  headers: documentHeaders,
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      ...socialMeta({
        title: TITLE,
        description: DESCRIPTION,
        path: "/about",
        lang: "en",
      }),
      { "script:ld+json": organizationJsonLd() },
      {
        "script:ld+json": breadcrumbJsonLd([
          { name: "Bible Shield", path: "/" },
          { name: "About", path: "/about" },
        ]),
      },
    ],
    links: [{ rel: "canonical", href: canonical("/about") }],
  }),
  component: About,
});

function Section({
  id,
  title,
  children,
}: {
  id: string;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section className="mt-12">
      <h2
        id={id}
        className="scroll-mt-24 font-display text-2xl font-bold text-ink"
      >
        {title}
      </h2>
      <div className="mt-4 flex flex-col gap-4 leading-[1.75] text-body">
        {children}
      </div>
    </section>
  );
}

function About() {
  return (
    <PageChrome>
      <div className="mx-auto w-full max-w-[68ch]">
        <p className="font-display text-xs font-bold tracking-[0.16em] text-scripture uppercase">
          about
        </p>
        <h1 className="mt-3 font-display text-[clamp(1.9rem,6vw,2.9rem)] leading-[1.15] font-bold tracking-tight text-ink text-balance">
          Who makes Bible Shield
        </h1>
        <p className="mt-4 text-[clamp(1.05rem,2.4vw,1.2rem)] leading-relaxed text-ink2">
          A short, checkable answer to the questions worth asking before you
          install something that stands between you and your phone.
        </p>

        <Section id="who" title="The company">
          <p>
            Bible Shield is built and published by {COMPANY}, a company
            registered in England and Wales, number {COMPANY_NO}, at{" "}
            {COMPANY_ADDRESS}. That number is searchable at Companies House and
            it is the same one on the{" "}
            <a
              href="/privacy"
              className="font-semibold text-brand underline decoration-hair underline-offset-4"
            >
              privacy policy
            </a>{" "}
            and the{" "}
            <a
              href="/terms"
              className="font-semibold text-brand underline decoration-hair underline-offset-4"
            >
              terms
            </a>
            .
          </p>
          <p>
            It is a small team, not a large one, and there is no venture money
            behind it deciding what the app has to become.
          </p>
        </Section>

        <Section id="why" title="Why it exists">
          <p>
            Every tool in this category asks you to use your phone less. That is
            a hard thing to want at eleven at night, and it is why most of them
            stop working within a fortnight — a rule you can revoke in one tap
            is not a rule, it is a reminder, and reminders stop being visible.
          </p>
          <p>
            Bible Shield is built on a different bet: that a block which ends in
            something worth doing survives, and one that ends in a countdown
            does not. The apps you choose close at the times you choose, and
            what opens them again is a short prayer written for the day you are
            actually having. Nothing is confiscated and nothing is withheld —
            you always get through, and you get through having done something.
          </p>
        </Section>

        <Section id="money" title="How it is paid for">
          <p>
            By subscription, from the people who use it. There is no advertising
            in the app, nothing you write is sold, and the free version is a
            real one rather than a trial with the timer hidden — downloading it
            and praying every day costs nothing. Premium unlocks unlimited
            guided prayers and the full library.
          </p>
          <p>
            Billing is handled by Apple and Google, so cancelling happens in
            your own device settings and never requires asking us.
          </p>
        </Section>

        <Section id="data" title="What it does with what you write">
          <p>
            Your prayers, notes and highlights are yours. Nothing you pray is
            posted anywhere; the community is a separate place you choose to
            enter. You can delete your account and everything in it from inside
            the app or from{" "}
            <a
              href="/delete"
              className="font-semibold text-brand underline decoration-hair underline-offset-4"
            >
              this website
            </a>
            , without emailing anyone.
          </p>
          <p>
            The privacy policy names every company your data actually reaches,
            by name, with a link to each one's own policy — including the ones
            it is uncomfortable to list. That list is checked against the code
            by a test rather than maintained by memory.
          </p>
        </Section>

        <Section id="writing" title="About the writing">
          <p>
            The{" "}
            <a
              href="/blog"
              className="font-semibold text-brand underline decoration-hair underline-offset-4"
            >
              articles on this site
            </a>{" "}
            are published by {COMPANY} rather than under an individual byline.
            We would rather attribute them to the organisation that stands
            behind them than invent a persona, and everything in them is either
            something the app does — which you can check — or a description of
            how these habits fail, which you can test against your own last
            fortnight.
          </p>
          <p>
            If something in them is wrong, write to{" "}
            <a
              href={`mailto:${SUPPORT}`}
              className="font-semibold text-brand underline decoration-hair underline-offset-4"
            >
              {SUPPORT}
            </a>{" "}
            and it will be corrected rather than quietly left.
          </p>
        </Section>

        <Section id="contact" title="Getting in touch">
          <p>
            {SUPPORT}, read by a person. The{" "}
            <a
              href="/support"
              className="font-semibold text-brand underline decoration-hair underline-offset-4"
            >
              support page
            </a>{" "}
            answers the questions that arrive most often — locking, emergency
            unlocks, subscriptions and account deletion.
          </p>
        </Section>

        <StoreCta from="about" />
      </div>
    </PageChrome>
  );
}
